import { listActiveProjectsOptions } from "@/actions/project/handler";
import { CoachingApplicationDialog } from "@/components/coaching/CoachingApplicationDialog";
import { SupportSystem } from "@/components/project/SupportSystem";
import { Button } from "@/components/ui/button";
import { createWhatsAppUrl } from "@/lib/whatsapp";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import * as Icons from "lucide-react";
import {
  ArrowRight,
  CheckCircle,
  FolderOpen,
  GraduationCap,
  MessageCircle,
} from "lucide-react";

export const Route = createFileRoute("/_app/projects")({
  head: () => ({
    title: "Projeler - Alican Atalay | Atalay The Coach",
    meta: [
      {
        name: "description",
        content:
          "Alican Atalay'ın yürüttüğü projeler, spor, koçluk, eğitim ve dijital dönüşüm alanlarında yenilikçi çalışmalar. Atalay The Coach ile güncel projeleri ve başarı hikayelerini keşfedin.",
      },
      {
        name: "keywords",
        content:
          "Alican Atalay projeler, Atalay The Coach, spor projeleri, koçluk projeleri, eğitim projeleri, dijital dönüşüm, başarı hikayeleri, Alican Atalay, online koçluk, sporcu gelişimi, inovasyon",
      },
    ],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(listActiveProjectsOptions());
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { user } = useRouteContext({ from: "/_app" });
  const { data } = useSuspenseQuery(listActiveProjectsOptions());
  const projects = data?.body?.projects || [];

  return (
    <div className="min-h-screen bg-background pt-28">
      <div className="container mx-auto px-4 pb-20">
        <div className="mb-16 text-center">
          <h1 className="mb-4 font-bold text-4xl text-foreground md:text-5xl">
            Projeler
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Topluma dokunan ve dönüşüm yaratan projelerimizle sizlerin
            gelişimine katkı sağlamayı hedefliyoruz.
          </p>
        </div>

        <div className="mx-auto max-w-5xl space-y-12">
          {projects.map((project) => {
            const IconComponent =
              project.icon && project.icon in Icons
                ? (Icons as unknown as Record<string, typeof GraduationCap>)[
                    project.icon
                  ]
                : GraduationCap;
            const features = Array.isArray(project.features)
              ? project.features
              : [];

            return (
              <div
                key={project.projectId}
                className="overflow-hidden rounded-xl border border-border bg-card transition-all hover:shadow-md"
              >
                <div className="flex flex-col md:flex-row">
                  {/* Media/Icon Area */}
                  <div className="flex h-56 w-full shrink-0 items-center justify-center overflow-hidden border-border border-b bg-muted/30 md:h-auto md:w-80 md:border-r md:border-b-0">
                    {project.imageUrl ? (
                      <img
                        src={project.imageUrl}
                        alt={project.title}
                        className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    ) : (
                      <IconComponent className="h-16 w-16 text-primary" />
                    )}
                  </div>

                  {/* Content Area */}
                  <div className="flex flex-1 flex-col p-6 sm:p-8">
                    <div className="mb-4 flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        {project.subtitle && (
                          <p className="font-semibold text-primary text-xs uppercase tracking-wider">
                            {project.subtitle}
                          </p>
                        )}
                        <h2 className="font-bold text-2xl text-foreground leading-tight">
                          {project.title}
                        </h2>
                      </div>
                    </div>

                    <p className="mb-6 text-[15px] text-muted-foreground leading-relaxed sm:text-base">
                      {project.description}
                    </p>

                    {features.length > 0 && (
                      <div className="mb-8 grid grid-cols-1 gap-y-2 sm:grid-cols-2">
                        {features.map((feature: string, idx: number) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 text-muted-foreground text-sm"
                          >
                            <CheckCircle className="h-4 w-4 text-primary/70" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-auto pt-4">
                      <SupportSystem
                        projectId={project.projectId}
                        projectTitle={project.title}
                        user={user}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {projects.length === 0 && (
            <div className="rounded-3xl border border-dashed py-20 text-center">
              <FolderOpen className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 font-semibold text-foreground text-xl">
                Henüz Proje Bulunmuyor
              </h3>
              <p className="text-muted-foreground">
                Yeni projelerimiz çok yakında burada olacak.
              </p>
            </div>
          )}

          {projects.length > 0 && (
            <section className="rounded-2xl border border-primary/20 bg-primary/5 p-6 text-center sm:p-8">
              <p className="mb-2 font-semibold text-primary text-sm">
                Gelişiminizi bir sonraki adıma taşıyın
              </p>
              <h2 className="font-bold text-2xl tracking-tight sm:text-3xl">
                Projelerden ilham alın, kendi planınızı oluşturalım
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
                Hedeflerinizi ve mevcut seviyenizi değerlendirelim; size uygun
                koçluk yaklaşımını birlikte belirleyelim.
              </p>
              <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                <CoachingApplicationDialog packageTitle="Projelerden Koçluk Başvurusu">
                  <Button size="lg" data-analytics-cta="projects_coaching">
                    Koçluk Başvurusu Yap
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CoachingApplicationDialog>
                <Button asChild size="lg" variant="outline">
                  <a
                    href={createWhatsAppUrl(
                      "Merhaba, projelerinizi inceledim ve koçluk hizmetleri hakkında bilgi almak istiyorum.",
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    WhatsApp'tan Sor
                  </a>
                </Button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
